/**
 * Houseplant v2 — FAQ contents list
 *
 * Drives sections/hp-faq-toc.liquid. Three jobs:
 *
 *   1. BUILD the list from the FAQ sections actually on the page, rather than
 *      from typed-in blocks that could drift from the headings they name.
 *   2. MEASURE the span those sections cover, so the section's own box can be
 *      that tall while contributing nothing to the flow — which is what lets
 *      `position: sticky` work across siblings it cannot wrap. See the note in
 *      the Liquid.
 *   3. TRACK which group is being read, and mark it.
 *
 * Everything here is enhancement. With the script absent the nav is empty and
 * collapses, the span variable is unset so the height and its negative margin
 * both resolve to 0, and the page reads exactly as it would without a contents
 * list at all.
 */
class HpFaqToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-hp-faq-toc-list]');
    if (!this.list) return;

    /* FAQ groups AFTER this element in the document. Scoped that way so a page
       with two contents lists (unlikely, but cheap to be correct about) does not
       have both of them describe every group on the page. */
    this.groups = Array.from(document.querySelectorAll('.hp-faq')).filter(
      (el) => this.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );

    if (this.groups.length < 2) {
      /* One group needs no contents list, and none certainly doesn't. Removing
         the whole section is better than leaving an empty nav taking up the
         gutter. */
      this.remove();
      return;
    }

    this.build();
    this.measure();
    this.observe();

    this.onResize = this.debounce(() => this.measure(), 150);
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /** Where the nav rests when stuck — the reading line. */
  stickyTop() {
    const nav = this.querySelector('.hp-faq-toc__nav');
    const value = nav ? parseFloat(getComputedStyle(nav).insetBlockStart) : NaN;
    return Number.isFinite(value) ? value : 120;
  }

  build() {
    this.links = this.groups.map((group, i) => {
      const heading = group.querySelector('.hp-faq__heading');
      /* A group with no heading has nothing to call itself; fall back to its
         position rather than rendering a blank row. */
      const label = heading?.textContent.trim() || `Section ${i + 1}`;

      /* Link to the section wrapper Shopify already gives an id, so the anchor
         survives a re-render. Only mint one if the markup somehow has none. */
      const target = group.closest('.shopify-section') || group;
      if (!target.id) target.id = `HpFaqGroup-${i + 1}`;

      const li = document.createElement('li');
      li.className = 'hp-faq-toc__item';

      const a = document.createElement('a');
      a.className = 'hp-faq-toc__link';
      a.href = `#${target.id}`;
      a.textContent = label;
      li.append(a);
      this.list.append(li);

      return { a, target };
    });
  }

  /**
   * Height + an equal negative margin: a real, tall box that contributes
   * nothing to the flow, so sticky has somewhere to travel. Measured from the
   * first group's top to the last group's bottom, in page coordinates so the
   * current scroll position can't skew it.
   */
  measure() {
    const first = this.groups[0].getBoundingClientRect();
    const last = this.groups[this.groups.length - 1].getBoundingClientRect();
    const span = Math.max(0, Math.round(last.bottom - first.top));
    this.style.setProperty('--hp-toc-span', `${span}px`);
  }

  /**
   * Scroll-spy. rootMargin pulls the detection band up to roughly the point the
   * sticky nav sits at, so the highlight changes when a heading reaches the
   * nav rather than when it reaches the very bottom of the viewport.
   *
   * The LAST entry to be intersecting wins, walked in document order — with
   * several short groups on screen at once, the one furthest down the page is
   * the one the reader has just arrived at.
   */
  /**
   * Mark the group being read.
   *
   * The LAST group whose heading has passed the READING LINE wins.
   *
   * The line is the nav's own resting position, not the middle of the viewport.
   * Mid-viewport is the obvious choice and it is wrong here: with every answer
   * collapsed the groups are only ~200 tall, so three or four sit above the
   * halfway mark at once and the highlight runs ahead of the reader. Jumping to
   * "Returns & Refunds" lit up "Shipping Policy". Anchoring to the nav means the
   * entry lights up exactly when its heading draws level with it.
   *
   * The 40 is a slack margin so a heading sitting a hair below the line still
   * counts as arrived.
   */
  update() {
    const line = this.stickyTop() + 40;

    let active = 0;
    this.groups.forEach((g, i) => {
      const rect = g.getBoundingClientRect();
      if (rect.top <= line && rect.bottom > 0) active = i;
    });

    if (active === this.active) return;
    this.active = active;

    this.links.forEach(({ a }, i) => {
      // Removed rather than set to "false": aria-current="false" is still an
      // announced state in some screen readers.
      if (i === active) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }

  /**
   * Driven by BOTH an observer and a scroll listener, deliberately.
   *
   * IntersectionObserver alone leaves nothing marked until a boundary is
   * crossed — on a page loaded part-way down, or in a background/hidden
   * document where rendering is throttled, the callback may not run at all and
   * the list sits with no active entry. (Measured: -1, no entry marked, after
   * scrolling four groups down.) The scroll listener guarantees it tracks; the
   * observer keeps it cheap by handling the common case.
   *
   * Throttled on a timestamp rather than requestAnimationFrame — rAF is
   * throttled in a hidden tab, which is exactly the case that broke it. Same
   * reasoning as the carousel's FLIP.
   */
  observe() {
    this.active = null;

    this.observer = new IntersectionObserver(() => this.update(), {
      threshold: [0, 0.01, 0.5, 1],
      rootMargin: '0px 0px -50% 0px',
    });
    this.groups.forEach((g) => this.observer.observe(g));

    let last = 0;
    this.onScroll = () => {
      const now = Date.now();
      if (now - last < 100) return;
      last = now;
      this.update();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // Mark something immediately, before any scrolling happens.
    this.update();
  }
}

if (!customElements.get('hp-faq-toc')) {
  customElements.define('hp-faq-toc', HpFaqToc);
}
